import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select, type SelectOption } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { RelayCard } from '@/components/RelayCard';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useSettingsStore, type RelayPreference, type Theme } from '@/store/settings';
import { useChatStore } from '@/store/chat';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { AUTO_LOCK_OPTIONS, autoLockLabel } from '@/config';
import { ROUTE_PATH } from '@/router';

const THEME_OPTIONS: SelectOption[] = [
  { value: 'dark', label: '深色' },
  { value: 'light', label: '浅色' },
];

const RELAY_OPTIONS: SelectOption[] = [
  { value: 'auto', label: '自动' },
  { value: 'direct', label: '仅直连' },
  { value: 'relay', label: '经中继' },
];

/**
 * DP Chain 控制台根地址（局域网独立入口，端口 8099：根路径=链浏览器/管理台，
 * /wallet.html=独立参与方钱包）。跟随当前页面的 host，桌面/手机同一套地址。
 */
const CHAIN_CONSOLE =
  typeof location !== 'undefined'
    ? `${location.protocol}//${location.host}/console/`
    : 'https://mesh.doup.cn/console/';

/** 设置页：外观 / 隐私 / 网络 / 链 / 存储 / 账户 / 黑名单。 */
export function SettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const s = useSettingsStore();
  const registered = useIdentityStore((st) => st.registered);
  const blocked = useChatStore((st) => st.contacts.filter((c) => c.blocked));

  const [blockedWordsText, setBlockedWordsText] = useState(s.blockedWords.join(','));
  const [cacheText, setCacheText] = useState(String(s.cacheLimitMB));
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDestroy, setConfirmDestroy] = useState(false);
  const [busy, setBusy] = useState(false);

  const registerDid = async (): Promise<void> => {
    setBusy(true);
    try {
      await container.identity.registerDid();
      useUiStore.getState().pushToast('success', 'DID 已注册');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const clearCache = async (): Promise<void> => {
    setConfirmClear(false);
    try {
      await container.storage.blobs.clear();
      useUiStore.getState().pushToast('success', '图片缓存已清除');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  const destroy = async (): Promise<void> => {
    setConfirmDestroy(false);
    setBusy(true);
    try {
      await container.identity.destroy();
      useIdentityStore.getState().setNone();
      navigate(ROUTE_PATH.welcome, { replace: true });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (peerId: string): Promise<void> => {
    try {
      await container.chat.setBlocked(peerId, false);
      useUiStore.getState().pushToast('success', '已解除拉黑');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">外观</h2>
        <div className="flex items-center justify-between">
          <span className="text-xs text-dim">主题</span>
          <Select value={s.theme} onChange={(v) => s.setTheme(v as Theme)} options={THEME_OPTIONS} />
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">隐私</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-dim">隐身（不出现在在线列表）</span>
            <Toggle checked={s.stealth} onChange={() => s.toggleStealth()} label="隐身" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-dim">发送已读回执</span>
            <Toggle checked={s.readReceipts} onChange={(v) => s.setReadReceipts(v)} label="已读回执" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-dim">仅直连（不经中继）</span>
            <Toggle checked={s.directOnly} onChange={(v) => s.setDirectOnly(v)} label="仅直连" />
          </div>
          <div>
            <span className="text-xs text-dim">屏蔽词（逗号分隔）</span>
            <div className="mt-1 flex gap-2">
              <Input value={blockedWordsText} onChange={(e) => setBlockedWordsText(e.target.value)} placeholder="如：广告,兼职" />
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  s.setBlockedWords(
                    blockedWordsText
                      .split(',')
                      .map((w) => w.trim())
                      .filter(Boolean),
                  )
                }
              >
                保存
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">网络</h2>
        <div className="flex items-center justify-between">
          <span className="text-xs text-dim">传输偏好</span>
          <Select
            value={s.relayPreference}
            onChange={(v) => s.setRelayPreference(v as RelayPreference)}
            options={RELAY_OPTIONS}
          />
        </div>
        <p className="mt-3 text-[11px] text-dim">中继连接状态与「切换中继」入口：</p>
        <RelayCard />
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">链</h2>
        <p className="mb-3 text-[11px] text-dim">
          钱包 / 存证 / 关注均已接入 DP Chain 真实链（本页网络状态即链后端状态）。链管理与独立钱包为同网独立入口。
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            className="inline-flex items-center rounded-btn border border-line px-3 py-1.5 text-xs text-text hover:border-accent"
            href={CHAIN_CONSOLE}
            target="_blank"
            rel="noreferrer"
          >
            链浏览器
          </a>
          <a
            className="inline-flex items-center rounded-btn bg-panel-2 px-3 py-1.5 text-xs text-text hover:bg-line"
            href={`${CHAIN_CONSOLE}wallet.html`}
            target="_blank"
            rel="noreferrer"
          >
            独立钱包
          </a>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">存储</h2>
        <div className="flex items-center gap-2">
          <Input
            value={cacheText}
            onChange={(e) => setCacheText(e.target.value)}
            placeholder="缓存上限（MB）"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const n = Number(cacheText);
              if (Number.isFinite(n) && n > 0) s.setCacheLimitMB(Math.trunc(n));
            }}
          >
            保存
          </Button>
        </div>
        <Button variant="danger" size="sm" className="mt-3" onClick={() => setConfirmClear(true)}>
          清除图片缓存
        </Button>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">账户</h2>
        <div className="flex items-center justify-between">
          <span className="text-xs text-dim">自动锁定</span>
          <Select
            value={String(s.autoLockMinutes)}
            onChange={(v) => s.setAutoLockMinutes(Number(v))}
            options={AUTO_LOCK_OPTIONS.map((m) => ({ value: String(m), label: autoLockLabel(m) }))}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {registered ? null : (
            <Button variant="outline" size="sm" loading={busy} onClick={() => void registerDid()}>
              注册 DID
            </Button>
          )}
          <Button variant="danger" size="sm" onClick={() => setConfirmDestroy(true)}>
            注销账户
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">黑名单</h2>
        {blocked.length === 0 ? (
          <p className="text-xs text-dim">暂无拉黑用户</p>
        ) : (
          <ul className="space-y-2">
            {blocked.map((c) => (
              <li key={c.peerId} className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs">
                <span className="truncate text-text">{c.name}</span>
                <Button variant="ghost" size="sm" onClick={() => void unblock(c.peerId)}>
                  解除
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={confirmClear}
        title="清除图片缓存"
        description="将删除本机缓存的图片内容，不影响链上数据。"
        danger
        onConfirm={() => void clearCache()}
        onCancel={() => setConfirmClear(false)}
      />

      <ConfirmDialog
        open={confirmDestroy}
        title="注销账户"
        description="将永久删除本机身份与全部本地数据，且无法恢复。请确认已备份助记词。"
        danger
        confirmText="永久删除"
        onConfirm={() => void destroy()}
        onCancel={() => setConfirmDestroy(false)}
      />
    </div>
  );
}

export default SettingsPage;
